"use client"

import { phaseColorMap, PhasePill } from "@/lib/phases"
import { ReactNode } from "react"
import { ProseContainer } from "../common"
import { iconMap } from "./iconMap"
import { PhaseTag } from "./PhaseTag"

type CategoryItem = {
  icon: string
  title: string
  items: string[]
}

type PhaseCardProps = {
  color: "blue" | "yellow" | "green"
  icon: string
  title: string
  subtitle: string
  children: ReactNode
  className?: string
  categories?: CategoryItem[]
  diagram?: ReactNode
  pills?: PhasePill[] // Use the imported PhasePill type
}

export function PhaseCard({
  color,
  icon,
  title,
  subtitle,
  children,
  className = "",
  categories,
  diagram,
  pills,
}: PhaseCardProps) {
  const colorStyles = phaseColorMap[color]
  const Icon = iconMap[icon]

  // Generate a small mesh pattern for the background
  const meshPatternId = `mesh-pattern-${title.toLowerCase()}`

  return (
    <div
      className={`relative rounded-xl border p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md ${colorStyles.bg} ${colorStyles.border} ${className}`}
      id={`phase-${title.toLowerCase()}`}
    >
      {/* Semantic mesh background */}
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl opacity-[0.03]"
        aria-hidden="true"
      >
        <svg
          width="100%"
          height="100%"
          xmlns="http://www.w3.org/2000/svg"
        >
          <pattern
            id={meshPatternId}
            x="0"
            y="0"
            width="20"
            height="20"
            patternUnits="userSpaceOnUse"
          >
            <circle
              cx="10"
              cy="10"
              r="0.5"
              fill="currentColor"
            />
            <circle
              cx="0"
              cy="10"
              r="0.5"
              fill="currentColor"
            />
            <circle
              cx="20"
              cy="10"
              r="0.5"
              fill="currentColor"
            />
            <circle
              cx="10"
              cy="0"
              r="0.5"
              fill="currentColor"
            />
            <circle
              cx="10"
              cy="20"
              r="0.5"
              fill="currentColor"
            />
            <line
              x1="10"
              y1="10"
              x2="0"
              y2="10"
              stroke="currentColor"
              strokeWidth="0.25"
            />
            <line
              x1="10"
              y1="10"
              x2="20"
              y2="10"
              stroke="currentColor"
              strokeWidth="0.25"
            />
            <line
              x1="10"
              y1="10"
              x2="10"
              y2="0"
              stroke="currentColor"
              strokeWidth="0.25"
            />
            <line
              x1="10"
              y1="10"
              x2="10"
              y2="20"
              stroke="currentColor"
              strokeWidth="0.25"
            />
          </pattern>
          <rect
            width="100%"
            height="100%"
            fill={`url(#${meshPatternId})`}
          />
        </svg>
      </div>

      <div className="mb-3 flex items-center gap-3">
        <div
          className={`flex h-12 w-12 items-center justify-center rounded-full ${colorStyles.iconBg} shadow-sm`}
        >
          {Icon && <Icon className={`h-6 w-6 ${colorStyles.text}`} />}
        </div>
        <div>
          <h3 className={`text-xl font-bold ${colorStyles.text}`}>{title}</h3>
          <p className="text-base font-medium text-gray-700 dark:text-gray-300">
            {subtitle}
          </p>
        </div>
      </div>

      <ProseContainer
        size="default"
        className="mb-3 max-w-none"
      >
        {children}
      </ProseContainer>

      {/* Optional categorized content - more compact */}
      {categories && categories.length > 0 && (
        <div className="mt-4 space-y-3">
          {categories.map((category, index) => {
            const CategoryIcon = iconMap[category.icon]
            return (
              <div
                key={index}
                className={`rounded-lg ${colorStyles.bgLight} p-3`}
              >
                <div className="mb-1.5 flex items-center gap-2">
                  {CategoryIcon && (
                    <CategoryIcon className={`h-5 w-5 ${colorStyles.text}`} />
                  )}
                  <h4 className={`font-semibold ${colorStyles.text}`}>
                    {category.title}
                  </h4>
                </div>
                <ProseContainer
                  size="default"
                  className="mb-1 ml-2"
                >
                  <ul>
                    {category.items
                      .filter(item => item != null && item !== "")
                      .map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                  </ul>
                </ProseContainer>
              </div>
            )
          })}
        </div>
      )}

      {/* Optional diagram or visual */}
      {diagram && <div className="mt-4 flex justify-center">{diagram}</div>}

      {/* Data-driven pills/tags using the interactive PhaseTag component */}
      {pills && pills.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {pills
            .filter(
              pill => pill != null && pill.label != null && pill.label !== ""
            )
            .map((pill, idx) => (
              <PhaseTag
                key={idx}
                label={pill.label}
                color={color}
              />
            ))}
        </div>
      )}
    </div>
  )
}
